import Router from 'next/router';
import api from 'api';
import { clearAuthenticatedSession } from 'lib/auth';

import type { Action } from 'redux';
import type {
  LocaleMessages,
  Locales,
  ReaderTheme,
  RequestUser,
  ServerConfig,
  Theme
} from 'shared-types';
import type { AppDispatch, PageState, PendingJobEntry } from 'types';

const addNotice = (data: {
  dismissAfter?: number;
  link?: { href: string; text: string };
  text: string;
  type?: 'error' | 'success' | 'warning';
}) =>
  ({
    type: 'addNotice' as const,
    data: { ...data, id: Date.now() }
  } as Action<'addNotice'>);

const dismissNotice = (id: number) =>
  ({
    type: 'dismissNotice' as const,
    id: id
  } as Action<'dismissNotice'>);

const setAccountDeleteRequests = (accountDeleteRequests: number) =>
  ({
    type: 'setAccountDeleteRequests' as const,
    accountDeleteRequests
  } as Action<'setAccountDeleteRequests'>);

const setLanguage =
  (language: Locales, messages: LocaleMessages) => (dispatch: AppDispatch) => {
    // save language in Cookie
    const aYearFromNow = new Date(
      new Date().setFullYear(new Date().getFullYear() + 1)
    );
    document.cookie = `language=${language};path=/;expires=${aYearFromNow}`;
    document.documentElement.setAttribute('lang', language);

    dispatch({
      type: 'setLanguage',
      language,
      messages
    } as Action<'setLanguage'>);
  };

const setInitialPageState = (pageState: Partial<PageState>) =>
  ({
    type: 'setInitialPageState',
    pageState: pageState
  } as Action<'setInitialPageState'>);

const setServerConfig = (serverConfig: Partial<ServerConfig>) =>
  ({
    type: 'setServerConfig' as const,
    serverConfig: serverConfig
  } as Action<'setServerConfig'>);

const setSupervisorUnreviewedItems = (unreviewedItems: number) =>
  ({
    type: 'setSupervisorUnreviewedItems' as const,
    supervisorUnreviewedItems: unreviewedItems
  } as Action<'setSupervisorUnreviewedItems'>);

const setUser = (user: Partial<RequestUser> | null) =>
  ({
    type: 'setUser' as const,
    user: user
  } as Action<'setUser'>);

const updateEditorPageState = (data: Partial<PageState>) =>
  ({
    type: 'updateEditorPageState' as const,
    data: data
  } as Action<'updateEditorPageState'>);

const updatePageState = (pageState: Partial<PageState>) =>
  ({
    type: 'updatePageState' as const,
    pageState: pageState
  } as Action<'updatePageState'>);

const updateReaderTheme = (id: number, data: Partial<ReaderTheme>) =>
  ({
    type: 'updateReaderTheme' as const,
    id: id,
    data: data
  } as Action<'updateReaderTheme'>);

const updateTheme = (id: number, data: Partial<Theme>) =>
  ({
    type: 'updateTheme' as const,
    id: id,
    data: data
  } as Action<'updateTheme'>);

const registerPendingJob = (data: {
  jobId: number;
  // Optional tailored copy; omit to fall back to per-type defaults.
  messages?: { failure: string; success: string };
  type: string;
}) =>
  ({
    type: 'registerPendingJob' as const,
    data
  } as Action<'registerPendingJob'>);

const removePendingJob = (jobId: number) =>
  ({
    type: 'removePendingJob' as const,
    jobId
  } as Action<'removePendingJob'>);

const resetPendingJobs = () =>
  ({ type: 'resetPendingJobs' as const } as Action<'resetPendingJobs'>);

const setPendingJobs = (jobs: Record<number, PendingJobEntry>) =>
  ({
    type: 'setPendingJobs' as const,
    jobs
  } as Action<'setPendingJobs'>);

const logout = (redirectUrl: string) => async (dispatch: AppDispatch) => {
  const response = await api.post('/auth/logout');
  if (response.data && response.data.success) {
    await clearAuthenticatedSession(dispatch);
    Router.push(response.data.redirectUrl || redirectUrl || '/login');
  }
  return response;
};

export const whoAmI = (cookie?: string) => async (dispatch: AppDispatch) => {
  try {
    const response = await api.get('/auth/whoami', {
      headers: {
        Accept: 'application/json',
        Cookie: cookie
      }
    });
    if (response.data && response.data.success) {
      dispatch(setUser(response.data.user));
    }
    return response.data.user as RequestUser;
  } catch (err) {}
};

export default {
  addNotice,
  dismissNotice,
  logout,
  registerPendingJob,
  removePendingJob,
  resetPendingJobs,
  setAccountDeleteRequests,
  setLanguage,
  setInitialPageState,
  setPendingJobs,
  setServerConfig,
  setSupervisorUnreviewedItems,
  setUser,
  updateEditorPageState,
  updatePageState,
  updateReaderTheme,
  updateTheme,
  whoAmI
};
